import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { ACCESS_TOKEN } from '../constants';
import api from '../api';
import { useState, useEffect, useMemo } from 'react';
import NotFound from './Notfound';
import { useTranslation } from '../LanguageContext';
import MediaGallery, { MediaTypeBadge } from '../components/MediaGallery';
import ReportModal from '../components/ReportModal';
import { RateCardView } from '../components/RateCard';
import { ReputationPanel } from '../components/ReputationBadge';
import { AvailabilityChip, hasAvailability } from '../components/AvailabilityPanel';
import Modal from '../components/Modal';
import ProfileFacets from '../components/ProfileFacets';
import { PlatformIcon, hostOf, platformLabel } from '../utils/platformIcons';
import { SubscribeModal } from '../components/SubscribePanel';
import { StripeIntentForm } from '../components/StripePayment';
import { publicProfileUrl } from '../utils/siteUrl';
import { ProfileBackdrop, themeVars, useThemeFont, resolveTheme } from '../utils/profileTheme';

/**
 * A visitor-side row: the same shape for a connected account and for a plain
 * link, so the profile reads as one list rather than three styles stacked.
 * The public page has no delete button, so the trailing slot carries the
 * follower count or the link's overflow menu instead.
 *
 * Everything about how this looks — fill, border, corner radius, shadow, text
 * colour — comes from `.pf-row` and the theme behind it. On a bio page the
 * rows *are* the buttons, so they are what the editor's button section moves.
 *
 * `icon` is the row's own picture when the owner uploaded one. Without it the
 * platform icon still gets derived from the host, and an unknown host still
 * gets its monogram tile — so a thumbnail upgrades a row and is never what
 * makes the row work.
 */
function PublicRow({ platform, url, title, subtitle, trailing, icon }) {
  return (
    <div
      onClick={() => url && window.open(url, '_blank', 'noopener,noreferrer')}
      className="group relative flex items-center gap-3 p-2.5 pf-row cursor-pointer"
    >
      {icon ? (
        <img
          src={icon}
          alt=""
          className="w-[42px] h-[42px] rounded-xl object-cover flex-shrink-0 ml-1.5"
          loading="lazy"
        />
      ) : (
        <PlatformIcon platform={platform} url={url} size={42} className="ml-1.5" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold pf-row-title truncate">{title}</p>
        {subtitle && <p className="text-[11px] pf-row-sub truncate">{subtitle}</p>}
      </div>
      {trailing}
    </div>
  );
}
const compactNumber = (value) => {
  const n = Number(value || 0);
  if (n >= 1000000) return `${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return `${n}`;
};

function User() {
  const { t } = useTranslation();
  const { username } = useParams();
  const acces = localStorage.getItem(ACCESS_TOKEN)
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [show, setShow] = useState(false);
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [isCoffeeModalOpen, setIsCoffeeModalOpen] = useState(false);
  const [coffeeName, setCoffeeName] = useState("");
  const [coffeeMessage, setCoffeeMessage] = useState("");
  const [coffeeAmount, setCoffeeAmount] = useState(1);
  const location = useLocation();
  const [showCoffeeSuccess, setShowCoffeeSuccess] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [contactError, setContactError] = useState('');
  const [showSubscribe, setShowSubscribe] = useState(false);
  // The card step of a coffee: `{ client_secret, publishable_key, amount_usd }`.
  const [coffeeIntent, setCoffeeIntent] = useState(null);
  const [coffeeBusy, setCoffeeBusy] = useState(false);
  const [coffeeError, setCoffeeError] = useState('');
  const [coffeeUnit, setCoffeeUnit] = useState(null);

  // The published appearance. Resolved up here rather than beside the markup
  // because the font it names is loaded by a hook, and the two early returns
  // further down would make that hook conditional.
  const theme = useMemo(() => resolveTheme(profile?.profile?.theme), [profile]);
  const vars = useMemo(() => themeVars(theme), [theme]);
  useThemeFont(theme);

  const copyProfileLink = () => {
    const profileLink = publicProfileUrl(username);
    navigator.clipboard.writeText(profileLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const getProfile = async () => {
    try {
      const res = await api.post('api/profile_data/', { username });
      setProfile(res.data);
      document.title = `${username}`;
      setLoading(false);
    }
    catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProfile();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('coffee_success') === 'true') {
      setShowCoffeeSuccess(true);
      
      // Temizle ki sayfa yenilendiğinde tekrar çıkmasın
      navigate(location.pathname, { replace: true });
      
      // 5 saniye sonra bildirimi gizle
      setTimeout(() => setShowCoffeeSuccess(false), 5000);
    }
  }, [location, navigate]);

  // What a coffee costs is the backend's number (COFFEE_UNIT_USD), which is
  // also what gets booked to the creator's ledger. The cup used to print a
  // hardcoded "€4" while the charge and the earning were both in dollars.
  useEffect(() => {
    if (!isCoffeeModalOpen || coffeeUnit !== null) return;
    api.get('/api/billing/config/')
      .then((res) => setCoffeeUnit(Number(res.data?.coffee_unit_usd) || 0))
      .catch(() => setCoffeeUnit(0));
  }, [isCoffeeModalOpen, coffeeUnit]);

  const addDialogue = async () => {
    setContactError('');
    try {
      const res = await api.post('api/add_dialogue/', { username });
      if (res.status === 200) {
        window.location.href = `/chats`;
      }
    }
    catch (error) {
      // A closed inbox is a deliberate setting, not a failure — name it,
      // otherwise the button just appears to do nothing.
      if (error.response?.data?.error === 'brand_only_messaging') {
        setContactError(t('user.brandOnlyInbox'));
      }
    }
  }

  // The payment happens in a sheet on this page. It used to be a redirect to a
  // hosted Stripe page, which on the mobile build meant leaving the app.
  const handleSubscribe = (e) => {
    e?.preventDefault();
    setShowSubscribe(true);
  };

  function RenderLinks() {
    return (
      <div className="mt-6">
        <hr className="pf-divider mb-4" />
        <div className="flex flex-col gap-2">
          {profile.links.map((item) => (
            <PublicRow
              key={item.id}
              url={item.link}
              title={item.title}
              subtitle={hostOf(item.link)}
              icon={item.icon}
              trailing={
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-full pf-row-sub hover:opacity-100 opacity-70 transition flex-shrink-0"
                  aria-label={t('user.showLink') || 'Show link'}
                  onClick={(e) => { e.stopPropagation(); handleShow(item.link); }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0" />
                  </svg>
                </button>
              }
            />
          ))}
        </div>
      </div>
    );
  }

  const handleClose = () => {
    setShow(false);
  }

  const handleShow = (link) => {
    setLink(link);
    setShow(true);
  }

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="spinner"></div></div>;
  }
  if (!profile) {
    return <NotFound />;
  }

  const subscriptionPriceUsd = Number(profile.profile?.subscription_price ?? 0);

  /**
   * Opens the payment for a coffee. The card form then replaces the cup in the
   * same overlay — a coffee can be bought by someone with no account at all, so
   * this deliberately never depends on being logged in.
   */
  const handlePayCoffee = async () => {
    if (coffeeBusy) return;
    setCoffeeBusy(true);
    setCoffeeError('');
    try {
      const res = await api.post('/api/billing/coffee/', {
        id: profile.id,
        coffee_name: coffeeName,
        coffee_message: coffeeMessage,
        coffee_amount: coffeeAmount,
      });
      if (res.data?.client_secret) {
        setCoffeeIntent(res.data);
      } else {
        setCoffeeError(t('billing.startError'));
      }
    } catch (error) {
      setCoffeeError(t('billing.startError'));
    }
    setCoffeeBusy(false);
  }

  /** Stripe took the money; tell the backend so the coffee lands right away. */
  const settleCoffee = async (intent) => {
    try {
      await api.post('/api/billing/purchase/confirm/', { payment_intent_id: intent.id });
    } catch (error) {
      // The webhook still delivers it — nothing is lost by not saying so here.
    }
    setCoffeeIntent(null);
    setIsCoffeeModalOpen(false);
    setCoffeeName('');
    setCoffeeMessage('');
    setCoffeeAmount(1);
    setShowCoffeeSuccess(true);
    setTimeout(() => setShowCoffeeSuccess(false), 5000);
    return null;
  }

  const closeCoffee = () => {
    setIsCoffeeModalOpen(false);
    setCoffeeIntent(null);
    setCoffeeError('');
  }


  return (
    <div className="relative min-h-screen pb-24 pf-scope" style={vars}>
      {/* Wallpaper — hidden while the media viewer is open to save GPU */}
      <ProfileBackdrop theme={theme} hidden={viewerOpen} />

      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-30 w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/30 transition border border-white/30 shadow-lg"
        title="Go back"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center pt-8 px-4">
        <div className="w-full max-w-md p-6 relative mt-4 pf-card">

          {/* Copy link + report. Report is muted by default and only reddens on
              hover — it is a rarely-correct action sitting next to a common one. */}
          <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
            <button
              onClick={copyProfileLink}
              className="w-10 h-10 rounded-full pf-soft pf-body flex items-center justify-center transition hover:opacity-80"
              title={t('user.copyLink') || 'Copy profile link'}
            >
              {copied ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              )}
            </button>

            {acces && (
              <button
                onClick={() => setShowReport(true)}
                className="w-10 h-10 rounded-full pf-soft pf-muted flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition"
                title={t('report.title')}
                aria-label={t('report.title')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                </svg>
              </button>
            )}
          </div>

          {/* Profile picture */}
          <div className="flex justify-center mb-4 pt-4">
            <div className="relative">
              <img
                className="w-20 h-20 pf-avatar ring-4 ring-white/50 object-cover shadow-lg"
                src={profile.profile.profile_picture}
                alt="profile"
              />
              {(profile.profile.is_brand || profile.profile.is_content_producer) && (
                <div
                  className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ring-2 ring-white ${profile.profile.verified ? (profile.profile.is_brand ? 'bg-yellow-500' : 'bg-blue-500') : 'bg-gray-400'}`}
                  title={profile.profile.verified ? (profile.profile.is_brand ? t('profile.verifiedBrand') : t('profile.verifiedCreator')) : (profile.profile.is_brand ? t('profile.unverifiedBrand') : t('profile.unverifiedCreator'))}
                >
                  {profile.profile.is_brand ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M10.067.87a2.89 2.89 0 0 0-4.134 0l-.622.638-.89-.011a2.89 2.89 0 0 0-2.924 2.924l.01.89-.636.622a2.89 2.89 0 0 0 0 4.134l.637.622-.011.89a2.89 2.89 0 0 0 2.924 2.924l.89-.01.622.636a2.89 2.89 0 0 0 4.134 0l.622-.637.89.011a2.89 2.89 0 0 0 2.924-2.924l-.01-.89.636-.622a2.89 2.89 0 0 0 0-4.134l-.637-.622.011-.89a2.89 2.89 0 0 0-2.924-2.924l-.89.01-.622-.636zM6.854 11.354l-2.5-2.5.708-.708L6.854 9.9l3.792-3.793.708.708-4.5 4.5z" />
                    </svg>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Name, then the three things a visitor came here to do — in that
              order, on their own line. Sharing a row with the name pushed the
              coffee button onto a second line at the first long username and
              made the order arbitrary. */}
          <h2 className="text-2xl font-bold pf-heading text-center mb-3">{username}</h2>

          <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
            {/* 1 — Get in touch.
                The gold metallic treatment is part of the *default* look, so
                it stays on the default theme and nothing changes for an account
                that never opened the editor. Once a theme is chosen, the theme
                owns the buttons — a gold slab in the middle of a monochrome or
                pastel page is the one element that would announce it was never
                designed with the rest. */}
            {theme.preset === 'classic' ? (
              acces ? (
                <button onClick={addDialogue} className="contact-btn-metallic">
                  <span className="contact-btn-metallic-inner">{t('user.contactBtn')}</span>
                </button>
              ) : (
                <Link to="/register" className="contact-btn-metallic no-underline">
                  <span className="contact-btn-metallic-inner">{t('user.contactBtn')}</span>
                </Link>
              )
            ) : (
              acces ? (
                <button
                  onClick={addDialogue}
                  className="pf-accent-btn px-5 py-2 font-extrabold text-sm shadow-sm transition hover:brightness-110 active:scale-95"
                >
                  {t('user.contactBtn')}
                </button>
              ) : (
                <Link
                  to="/register"
                  className="pf-accent-btn px-5 py-2 font-extrabold text-sm shadow-sm transition hover:brightness-110 no-underline"
                >
                  {t('user.contactBtn')}
                </Link>
              )
            )}

            {/* 2 — Media kit. The press page carries the numbers and the
                track record, so it is a peer of "get in touch" rather than a
                banner further down the page. */}
            {profile.media_kit && (
              <Link
                to={`/${username}/media-kit`}
                className="pf-row pf-row-title px-4 py-2 font-semibold transition flex items-center gap-2 no-underline"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
                <span className="text-sm font-extrabold">{t('mediaKit.title')}</span>
              </Link>
            )}

            {/* 3 — Buy a coffee, when the creator has it switched on */}
            {profile.profile.coffes_enabled && (
              <button
                onClick={() => setIsCoffeeModalOpen(true)}
                className="relative group bg-white border border-[#d2b48c] hover:bg-[#fff9f0] text-[#6f4e37] px-4 py-2 rounded-2xl font-semibold shadow-sm transition flex items-center gap-2 ml-1"
              >
                <span className="text-sm font-extrabold">{t('user.buyCoffee')}</span>
                {/* Tilted coffee icon */}
                <img
                  src="/coffee.png"
                  alt="Coffee"
                  className="absolute -top-3.5 -right-3 w-8 h-8 object-contain transform rotate-12 group-hover:rotate-[25deg] transition-transform duration-300 drop-shadow-sm pointer-events-none"
                />
              </button>
            )}
            {/* 4 — Subscribe. The only other way in is the padlock over a
                locked gallery, which a creator who does not restrict their
                media never shows — so for most creators there was no way to
                subscribe at all. Gated on `profile.gallery` being present,
                which is exactly the case where that padlock is *not* drawn:
                one entry point on the page, never two. */}
            {profile.profile.is_content_producer
              && profile.profile.enable_subscriptions
              && profile.is_subscribed !== true
              && Boolean(profile.gallery) && (
                <button
                  className="subscribe-btn-silver"
                  onClick={acces ? handleSubscribe : () => navigate('/login')}
                >
                  <span className="subscribe-btn-silver-inner">
                    {t('billing.subscribeNow')}
                    {subscriptionPriceUsd > 0 && (
                      <span className="ml-1.5 pl-1.5 border-l border-slate-400/40 font-black tabular-nums">
                        ${subscriptionPriceUsd.toLocaleString('en-US')}
                        <span className="opacity-70">{t('billing.perMonthShort')}</span>
                      </span>
                    )}
                  </span>
                </button>
              )}
          </div>

          {contactError && (
            <p className="text-center text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3 leading-relaxed">
              {contactError}
            </p>
          )}


          {/* Countries & tags, between the name and the bio */}
          <ProfileFacets
            countries={profile.profile.countrys}
            tags={profile.profile.tags}
            className="mb-3"
            themed
          />

          {/* Bio */}
          {profile.profile.profile_text && (
            <div className="pf-soft pf-body rounded-2xl p-4 mb-4 text-sm">
              {profile.profile.profile_text}
            </div>
          )}

          {/* Social Media */}
          {profile.socials && profile.socials.length > 0 && (
            <div className="w-full flex flex-col gap-2 mb-4">
              {profile.socials.map((item) => (
                <PublicRow
                  key={item.id}
                  platform={item.social_media}
                  url={item.link}
                  title={platformLabel(item.social_media)}
                  subtitle={item.username ? `@${String(item.username).replace(/^@/, '')}` : hostOf(item.link)}
                  trailing={
                    <span className="flex items-center gap-1 text-[11px] font-bold pf-row-title pf-soft px-2 py-1 rounded-lg flex-shrink-0 tabular-nums">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      {compactNumber(item.followers)}
                    </span>
                  }
                />
              ))}
            </div>
          )}

          {/* Reach & engagement — the numbers a brand actually decides on,
              which a follower count on its own does not give them. */}
          {(profile.metrics?.avg_reach > 0 || profile.metrics?.engagement_rate > 0) && (
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="rounded-xl pf-soft p-2.5 text-center">
                <p className="text-base font-black pf-heading tabular-nums">
                  {compactNumber(profile.metrics.followers)}
                </p>
                <p className="text-[10px] pf-muted font-semibold">{t('profile.followers')}</p>
              </div>
              <div className="rounded-xl pf-soft p-2.5 text-center">
                <p className="text-base font-black pf-heading tabular-nums">
                  {compactNumber(profile.metrics.avg_reach)}
                </p>
                <p className="text-[10px] pf-muted font-semibold">{t('mediaKit.reach')}</p>
              </div>
              <div className="rounded-xl pf-soft p-2.5 text-center">
                <p className="text-base font-black pf-accent-text tabular-nums">
                  %{profile.metrics.engagement_rate}
                </p>
                <p className="text-[10px] pf-muted font-semibold">{t('mediaKit.engagement')}</p>
              </div>
            </div>
          )}

          {/* Availability — the answer to "can I even book them", so it goes
              above the press page rather than under it. A creator who has not
              stated one gets no row at all: the categories still earn one on
              their own, but an empty flex row is just a stray margin. */}
          {(hasAvailability(profile.availability)
            || (profile.collab_categories || []).length > 0) && (
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <AvailabilityChip availability={profile.availability} />
              {profile.availability?.note && (
                <span className="text-[11px] pf-muted italic">{profile.availability.note}</span>
              )}
              {(profile.collab_categories || []).map((category, index) => (
                <span key={index}
                  className="px-2 py-0.5 rounded-full pf-soft pf-body text-[10px] font-semibold">
                  {category}
                </span>
              ))}
            </div>
          )}

          {/* Track record. A creator's lives on their media kit now — the
              press page is where a brand reads the whole case, and repeating
              it here made the profile a second, worse copy of that page. A
              brand has no media kit to move it to, so theirs stays: a
              creator's whole reason for reading a brand profile is how that
              brand has paid and reviewed in the past. */}
          {!profile.profile.is_content_producer
            && profile.reputation
            && (profile.reputation.count > 0 || profile.reputation.facts) && (
              /* `pf-embed` because these blocks carry their own slate palette
                 and cannot be recoloured from the theme — on a dark card they
                 get a light panel to sit on, on a light one it is nothing. */
              <div className="mb-4 pf-embed">
                <ReputationPanel reputation={profile.reputation} />
              </div>
            )}

          {/* Published prices */}
          {profile.rate_card && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-bold pf-heading">{t('rateCard.title')}</h3>
                {profile.rate_card.starting_usd > 0 && (
                  <span className="text-[11px] font-bold pf-muted">
                    {t('rateCard.from')} ${profile.rate_card.starting_usd.toLocaleString('en-US')}
                  </span>
                )}
              </div>
              <div className="pf-embed">
                <RateCardView card={profile.rate_card} />
              </div>
            </div>
          )}

          {/* Links */}
          {profile.links && profile.links.length > 0 && <RenderLinks />}

          {/* Gallery Section */}
          {profile.profile.is_content_producer && (
            <div className="mt-6">
              <hr className="pf-divider mb-4" />


              {profile.gallery ? (
                profile.gallery.length > 0 ? (
                  /* State 1: Gallery exists with data */
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-sm font-bold pf-heading">{t('creator.gallery')}</h3>
                      <span className="text-[11px] font-semibold pf-muted tabular-nums">
                        {profile.gallery.length}
                      </span>
                    </div>
                    {/* Same grid the creator sees on their own page: 4:5 tiles
                        with the type pill, so what a creator arranges is what a
                        visitor gets. Square thumbnails cropped every portrait
                        upload — which is most of them — through the middle. */}
                    <MediaGallery
                      items={profile.gallery}
                      columns="grid-cols-2 sm:grid-cols-3"
                      aspect="portrait"
                      gap="gap-3"
                      onOpenChange={setViewerOpen}
                      renderBadge={(item) => (
                        <MediaTypeBadge
                          type={item.type}
                          className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        />
                      )}
                    />
                  </>
                ) : (
                  /* State 2: Gallery exists but empty */
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 mb-2 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                    </svg>
                    {/* <p className="text-sm font-medium">Media element not found</p> */}
                  </div>
                )
              ) : (
                /* State 3: No gallery at all — image + subscribe */
                <div className="relative rounded-xl overflow-hidden">
                  <img
                    src="https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/ed356b2a-a151-498c-d6fc-545c6bf9c200/public"
                    alt=""
                    className="w-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    {profile.profile.enable_subscriptions && (
                      <button
                        onClick={acces ? handleSubscribe : () => navigate("/login")}
                        className="relative px-8 py-3.5 rounded-full font-bold text-white text-sm tracking-wide overflow-hidden transition-all duration-300 hover:scale-105 active:scale-95"
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          boxShadow: '0 0 20px rgba(102, 126, 234, 0.5), 0 0 40px rgba(118, 75, 162, 0.3), 0 4px 15px rgba(0,0,0,0.2)',
                        }}
                      >
                        <span className="relative z-10 flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          {!acces ? t('landing.login') || "Login" : (t('user.unlock') || "Kilidi Aç / Unlock")}
                          {/* The price belongs on the button. It is the
                              creator's own figure now, so it is not something
                              the reader could already know. */}
                          {acces && subscriptionPriceUsd > 0 && (
                            <span className="ml-1 pl-2 border-l border-white/30 font-black tabular-nums">
                              ${subscriptionPriceUsd.toFixed(2)}
                              <span className="font-semibold opacity-80">{t('billing.perMonthShort')}</span>
                            </span>
                          )}
                        </span>
                        <div
                          className="absolute inset-0 opacity-60"
                          style={{
                            background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 2.5s ease-in-out infinite',
                          }}
                        />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Where a link actually goes. Worth its own dialog: the row shows
              a title the profile owner wrote, and seeing the real destination
              before following it is the whole point of the button. */}
          <Modal
            open={show}
            onClose={handleClose}
            title={t('user.linkDestination')}
            size="sm"
            footer={
              <div className="flex gap-3">
                <button type="button" className="btn-secondary flex-1 py-2.5 text-sm" onClick={handleClose}>
                  {t('user.closeBtn')}
                </button>
                <a href={link} target="_blank" rel="noopener noreferrer"
                  className="btn-primary flex-1 py-2.5 text-sm text-center no-underline" onClick={handleClose}>
                  {t('user.openLink')}
                </a>
              </div>
            }
          >
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <PlatformIcon url={link} size={40} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-800 truncate">{hostOf(link)}</p>
                <p className="text-[11px] text-slate-400 break-all">{link}</p>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">{t('user.linkWarning')}</p>
          </Modal>

          <div className="my-10"></div>
        </div>
      </div>

      {/* Coffee Modal — the cup while the note is being written, the card form
          in its place once payment starts. Two overlays stacked would have put
          a second scrim over the first, and the cup is 540px of artwork that
          nobody needs to look at while typing a card number. */}
      {isCoffeeModalOpen && coffeeIntent && (
        <div className="fixed inset-0 z-[9600] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
            <div className="flex items-start justify-between px-6 pt-6 pb-3 flex-shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <img src="/coffee256.png" alt="" className="w-8 h-8 object-contain flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-lg font-black text-slate-900">{t('user.buyCoffee')}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {coffeeAmount} × @{username}
                  </p>
                </div>
              </div>
              <button
                onClick={closeCoffee}
                className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 transition flex-shrink-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 pb-6 overflow-y-auto custom-scrollbar">
              <StripeIntentForm
                clientSecret={coffeeIntent.client_secret}
                publishableKey={coffeeIntent.publishable_key}
                amountUsd={coffeeIntent.amount_usd}
                submitLabel={t('user.payCoffee')}
                onSucceeded={settleCoffee}
                onCancel={() => setCoffeeIntent(null)}
                cancelLabel={t('user.closeBtn')}
              />
            </div>
          </div>
        </div>
      )}

      {isCoffeeModalOpen && !coffeeIntent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={closeCoffee} />

          <div className="relative z-10 flex flex-col items-center justify-center animate-in fade-in zoom-in duration-300">
            {/* Close button */}
            <button
              onClick={closeCoffee}
              className="absolute -top-4 -right-4 sm:-top-8 sm:-right-8 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full text-white flex items-center justify-center backdrop-blur-md transition shadow-xl"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* The giant coffee cup image */}
            <div className="relative w-[420px] sm:w-[540px]">
              <img src="/coffee512.png" alt="Buy Coffee" className="w-full h-auto drop-shadow-2xl pointer-events-none" />

              {/* The form area on the turquoise part of the cup */}
              <div className="absolute top-[44%] left-[22%] right-[22%] h-[42%] flex flex-col items-center justify-start gap-2 sm:gap-3 z-10">
                {/* Inputs looking like a coffee label */}
                <input
                  type="text"
                  value={coffeeName}
                  onChange={e => setCoffeeName(e.target.value)}
                  placeholder={t('user.coffeeName') || 'İsim (Opsiyonel)'}
                  className="w-full bg-transparent border-b border-[#2b6b66]/30 placeholder-[#2b6b66]/70 text-[#0d2e2b] text-center font-black focus:outline-none focus:border-[#1a4a47] focus:bg-white/10 transition px-2 py-1 text-sm sm:text-base"
                />
                <textarea
                  value={coffeeMessage}
                  onChange={e => setCoffeeMessage(e.target.value)}
                  placeholder={t('user.coffeeMessage') || 'Mesajınız...'}
                  className="w-[calc(100%-23px)] flex-1 bg-transparent border-b border-[#2b6b66]/30 placeholder-[#2b6b66]/60 text-[#1a4a47] text-center font-bold focus:outline-none focus:border-[#1a4a47] focus:bg-white/10 transition px-2 py-2 text-sm sm:text-base resize-none overflow-hidden"
                />
                <div className="relative flex items-center justify-between gap-1 w-full px-4 mt-auto mb-5">
                  <button
                    onClick={() => setCoffeeAmount(Math.max(1, coffeeAmount - 1))}
                    className="bg-white/40 hover:bg-white/60 text-[#1a4a47] w-7 h-7 sm:w-8 sm:h-8 rounded-full font-bold transition flex items-center justify-center shadow-sm text-base"
                  >-</button>
                  <span className="text-[#1a4a47] font-black text-base sm:text-lg w-8 text-center">{coffeeAmount}</span>
                  <button
                    onClick={() => setCoffeeAmount(coffeeAmount + 1)}
                    className="bg-white/40 hover:bg-white/60 text-[#1a4a47] w-7 h-7 sm:w-8 sm:h-8 rounded-full font-bold transition flex items-center justify-center shadow-sm text-base"
                  >+</button>

                  {/* Tilted Ticket — the real total, in the currency the card
                      is actually charged in. */}
                  <div className="absolute -bottom-[45px] left-1/2 -translate-x-1/2 bg-[#fdfbf7] border-[2px] border-dashed border-[#c2a781] transform -rotate-[6deg] px-4 py-1 shadow-[0_4px_6px_rgba(0,0,0,0.1)] rounded-sm whitespace-nowrap pointer-events-none">
                    <span className="font-black text-[#4a3424] text-lg sm:text-xl drop-shadow-sm">
                      {coffeeUnit === null ? '···' : `$${(coffeeAmount * coffeeUnit).toFixed(2)}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Pay Button below the cup */}
            <button
              onClick={handlePayCoffee}
              disabled={coffeeBusy}
              className="mt-6 bg-gradient-to-r from-[#8B5A2B] to-[#6f4e37] hover:from-[#6f4e37] hover:to-[#573d2a] text-white px-8 py-3.5 rounded-2xl font-bold shadow-xl transition-all hover:scale-105 active:scale-95 disabled:opacity-60 disabled:hover:scale-100 flex items-center gap-2"
            >
              {coffeeBusy ? (
                <span className="spinner-sm" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {t('user.payCoffee') || `Gönder (${coffeeAmount} Kahve)`}
            </button>

            {coffeeError && (
              <p className="mt-3 text-xs text-white bg-red-500/90 border border-red-300/50 rounded-xl px-3 py-2 max-w-xs text-center">
                {coffeeError}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Coffee Success Notification Toast */}
      {showCoffeeSuccess && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] animate-in zoom-in-95 fade-in duration-300">
          <div className="bg-gradient-to-r from-[#2b6b66] to-[#1a4a47] text-white px-8 py-6 rounded-3xl shadow-[0_15px_50px_rgba(26,74,71,0.6)] flex flex-col items-center gap-4 border border-white/10 text-center min-w-[300px]">
            
            <button onClick={() => setShowCoffeeSuccess(false)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition text-white/80 hover:text-white">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <img src="/coffee256.png" alt="Coffee" className="w-24 h-24 object-contain drop-shadow-xl animate-bounce" style={{ animationDuration: '2s' }} />
            
            <div>
              <h4 className="font-black text-2xl mb-1">{t('user.coffeeThanksTitle') || 'Teşekkürler!'}</h4>
              <p className="text-white/90 text-sm font-medium">{t('user.coffeeThanksMessage') || 'Kahve başarıyla gönderildi.'}</p>
            </div>
          </div>
        </div>
      )}

      {showReport && (
        <ReportModal
          username={username}
          userId={profile?.id}
          onClose={() => setShowReport(false)}
        />
      )}

      {/* Subscribing to this creator, paid here rather than on a Stripe page.
          Re-reads the profile on success so whatever the subscription unlocks
          is on screen without a reload. */}
      {showSubscribe && (
        <SubscribeModal
          target="creator"
          creator={{ id: profile.id, username, price: subscriptionPriceUsd }}
          onClose={() => setShowSubscribe(false)}
          onSubscribed={getProfile}
        />
      )}
    </div>
  );
}

export default User;



// subscribe görünümlerini düzelt